import { useQueryClient } from '@tanstack/react-query';
import React, { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { css, styled } from 'styled-components';

import { Button } from '@dutchie-pos/components';
import { LD, useLDFlag } from '@dutchie-pos/utils';

import { PaymentType } from 'src/app/types/payments';
import ErrorIcon from 'src/assets/icon-error.svg?react';
import InfoIcon from 'src/assets/info.svg?react';
import PayByBankLogo from 'src/assets/pay-by-bank-logo.svg?react';
import { DutchieInput } from 'src/components/dutchie-input';
import { PaymentType as PaymentTypeModel } from 'src/models/checkout';
import { type CheckoutRequest, isDutchiePayCheckoutError, useCheckout } from 'src/queries/curbside/checkout';
import { GetCart, useCart } from 'src/queries/curbside/get-cart';
import { useDeliveryInfo } from 'src/queries/curbside/get-delivery-info';
import { GetOrdersForRouteKey } from 'src/queries/curbside/get-orders-for-route';
import { GuestListKey } from 'src/queries/curbside/guest-list';
import { useLocation } from 'src/queries/curbside/location-details';
import { usePay } from 'src/queries/curbside/pay';
import { FeePaymentMethod } from 'src/queries/curbside/types';
import { PollNotCompleteError, usePollPaymentQuery } from 'src/queries/payment/poll-payment';
import { useGeneriPayPaymentIntegrationsPOSQuery } from 'src/queries/v2/payments/generipay';
import { useEnabledManualPaymentProcessors } from 'src/queries/v2/payments/manual-payments';
import { usePrintReceipt } from 'src/queries/v2/print-jobs/print-receipt';
import {
  setActiveCart,
  setChangeAmountDue,
  setChangeAmountRounded,
  setDutchiePayErrorMessage,
  setIsCheckoutComplete,
  setIsDutchiePayError,
  setIsRethinkError,
  setRethinkErrorMessage,
  setTotalAmountPaid,
} from 'src/store/actions/MobileCheckoutActions';
import { errorNotification, successNotification } from 'src/store/actions/notifications-actions';
import { useAppDispatch, useAppSelector } from 'src/utils';
import { getChangeRounded, roundChangeDue } from 'src/utils/helpers/cart/rounding';
import { getFeeNameFromProcessor } from 'src/utils/helpers/payments/get-fee-name-from-processor';
import { usePrintWorker } from 'src/utils/hooks/printing/usePrintWorker';
import { useHasPreauth } from 'src/utils/hooks/use-has-preauth';
import { useIsDutchiePay } from 'src/utils/hooks/use-is-dutchie-pay';
import { useIsRethink } from 'src/utils/hooks/use-is-rethink';
import { usePbbInstorePayment } from 'src/utils/hooks/use-pbb-instore-payment';
import { customEventKeys, logger } from 'src/utils/logger';

import { CanPayCheckout } from './canpay-checkout';
import { CartSummary } from './cart-order-summary';
import { CartOrderTotal } from './cart-order-total';
import { DebitWarning } from './debit-warning';
import { ManualPaymentModal, type ManualPaymentSubmission } from './manual-payment-modal';
import { PayByBankInstoreQrScreen } from './pay-by-bank-instore-qr-screen';
import { type PaymentFormTotals } from './types';

type PaymentFormProps = {
  onFailure: () => void;
  onPaymentSuccess: () => void;
  totals: PaymentFormTotals;
  validationNeeded?: boolean;
};

type ElectronicPaymentIntegrationFields = {
  Token: string;
  PaymentIntegrationDefinitionId: string;
  ShipmentPaymentType: number;
  UseCartSync: boolean;
};

export const PaymentFormUpdated: FC<PaymentFormProps> = ({
  onFailure,
  onPaymentSuccess,
  totals,
  totals: { subtotal, tax, tip, total },
  validationNeeded,
}) => {
  // Global state
  const activeCart = useAppSelector((state) => state.mobileCheckout.activeCart);
  const selectedRegister = useAppSelector((state) => state.user.selectedRegister);

  // Get the most up-to-date tip amount from Redux store
  const updatedTip = activeCart?.TipAmount ?? tip;

  const selectedReceiptPrinterId = useAppSelector((state) => state.user.selectedRegister?.ReceiptPrinterId);
  const selectedReceiptPrinterGuid = useAppSelector((state) => state.user.selectedRegister?.DefaultReceiptPrinterGuid);
  const { isPrintWorkerEnabled } = usePrintWorker();

  const changeAmountDue = useAppSelector((state) => state.mobileCheckout.changeAmountDue);
  const isDutchiePayError = useAppSelector((state) => state.mobileCheckout.isDutchiePayError);
  const dutchiePayErrorMessage = useAppSelector((state) => state.mobileCheckout.dutchiePayErrorMessage);
  const isRethinkError = useAppSelector((state) => state.mobileCheckout.isRethinkError);
  const rethinkErrorMessage = useAppSelector((state) => state.mobileCheckout.rethinkErrorMessage);
  const selectedRouteId = useAppSelector((state) => state.user.selectedRouteId);

  // Local state
  const [cash, setCash] = useState(0);
  const [giftcardPaid, setGiftcardPaid] = useState(0);
  const [debit, setDebit] = useState(0);
  const [checkPaid, setCheckPaid] = useState(0);
  const [manualPayment, setManualPayment] = useState<ManualPaymentSubmission>();
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [generiPayPaid, setGeneriPayPaid] = useState<Record<string, string>>({});
  const [addedSurcharge, setAddedSurcharge] = useState(false);
  const [preauthAmt, setPreauthAmt] = useState(0);
  const [showCanPayCheckout, setShowCanPayCheckout] = useState(false);
  const [showPbbInstoreQr, setShowPbbInstoreQr] = useState(false);
  const [showPbbInstoreSuccess, setShowPbbInstoreSuccess] = useState(false);
  const [isPollingComplete, setIsPollingComplete] = useState(false);

  const [isPaid, setIsPaid] = useState(!!activeCart?.Payment?.ElectronicAmount || !!debit);
  const [electronicPaymentType, setElectronicPaymentType] = useState<string>();
  const [electronicPaid, setElectronicPaid] = useState<string>('');
  const [electronicPaymentData, setElectronicPaymentData] = useState<ElectronicPaymentIntegrationFields>({
    Token: '',
    PaymentIntegrationDefinitionId: '',
    ShipmentPaymentType: 2,
    UseCartSync: false,
  });
  const [canpayPaid, setCanpayPaid] = useState(() => {
    if (['canpay'].includes(activeCart?.Payment?.ElectronicType || '')) {
      return activeCart?.Payment?.ElectronicAmount ?? 0;
    }

    return 0;
  });

  // Refs
  // FINTECH-5030: bounds the automatic recovery retry in handlePollPayment's PollNotCompleteError
  // branch below, so a payment that genuinely never resolves can't retry forever.
  const pollRecoveryAttemptsRef = useRef(0);
  const cashInputRef = useRef<HTMLInputElement | null>(null);
  const debitInputRef = useRef<HTMLInputElement | null>(null);
  const giftCardInputRef = useRef<HTMLInputElement | null>(null);
  const checkInputRef = useRef<HTMLInputElement | null>(null);
  const generiPayInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const canPayInputRef = useRef<HTMLInputElement | null>(null);

  const { data: location } = useLocation();

  // Computed
  const integratedDebitProvider = location?.Integrations.IntegratedDebitProvider;
  const debitProcessor = location?.Integrations.DebitProcessor;
  const isAllowDutchiePayDeliveryFFEnabled = !!location?.Features.AllowDutchiePayDelivery;
  const isUseRethinkIntegrationEnabled = location?.Integrations.UseReThink;
  const isPaymentsHubEnabled = !!location?.Integrations.UsePaymentsHub;
  const isLocationPbbEnabled = !!location?.Integrations.UseDutchiePay;
  const isAcceptCanPayFFEnabled = !!location?.Features.AcceptCanPay;
  const isGeneriPaySpinEnabled = !!location?.Features.GeneriPaySpin;
  const isCurbsideCashlessFFEnabled = !!location?.Features.CurbsideCashless;
  const isCheckOutGiftCardFFEnabled = !!location?.Features.CheckOutGiftCard;
  // Matches register's own gate (usePaymentButtons.tsx: `features.CheckOutDebit`) - previously this
  // row had no feature-flag/integration check at all (unlike Cash/CanPay/Gift Card, which already
  // had one each), so Debit always showed up whenever the payment-method fallback UI was on screen,
  // regardless of whether this location's back-office "Checkout Debit" superuser setting was off.
  const isDebitEnabled = !!location?.Features.CheckOutDebit;
  // NOTE: FINTECH_MOBILE_CHECKOUT_CHECK_AND_MANUAL's name is misleading - it gates ONLY Check.
  const isCheckFFEnabled = useLDFlag(LD.FINTECH_MOBILE_CHECKOUT_CHECK_AND_MANUAL, false);
  const isManualFFEnabled = useLDFlag(LD.FINTECH_MOBILE_CHECKOUT_MANUAL, false);
  const isCheckEnabled = isCheckFFEnabled && !!location?.Features.CheckOutCheck;
  const isManualPaymentsFeatureEnabled = isManualFFEnabled && !!location?.Features.EnableManualPayments;
  const useUSAG = debitProcessor === 'usag';
  const paymentFeeName = getFeeNameFromProcessor(debitProcessor);
  // USAG transactions cannot be for over $200
  // SOP is to make the budtender split up into multiple transactions
  const debitDisabled = useUSAG && total >= 200;

  // Hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isDutchiePay = useIsDutchiePay();
  const isRethink = useIsRethink();
  const hasPreauth = useHasPreauth();
  const { shipmentId } = useParams();
  const queryClient = useQueryClient();
  const { data: generiPayButtons } = useGeneriPayPaymentIntegrationsPOSQuery(
    selectedRegister?.id,
    isGeneriPaySpinEnabled,
  );
  const { processors: enabledManualProcessors } = useEnabledManualPaymentProcessors(isManualPaymentsFeatureEnabled);
  const isPbbInstoreEnabled = useLDFlag(LD.FINTECH_MOBILE_CHECKOUT_PBB, false);
  const isPbbRefreshCartOnConfirmEnabled = useLDFlag(LD.FINTECH_MOBILE_CHECKOUT_PBB_REFRESH_CART, false);
  const isDutchiePayDeclineMessagingEnabled = useLDFlag(LD.FINTECH_MOBILE_CHECKOUT_ERROR_MESSAGING_UPDATES, false);
  const {
    instoreCartUrl,
    isLoading: isPbbInstoreLoading,
    isConfirming: isPbbInstoreConfirming,
    error: pbbInstoreError,
  } = usePbbInstorePayment({
    enabled: showPbbInstoreQr,
    onPaymentSuccess: () => {
      setShowPbbInstoreQr(false);
      setShowPbbInstoreSuccess(true);
      // The preauth is attached server-side before cart-confirmed fires, but nothing else
      // refetches the cart on this path, so the order/paid state renders stale. Refetch it
      // explicitly instead of relying on an incidental refetch (window focus/remount).
      // Gated so it can be enabled per-location and killed independently if needed.
      if (isPbbRefreshCartOnConfirmEnabled) {
        void queryClient.invalidateQueries({ queryKey: [GetCart, shipmentId] });
      }
    },
    shipmentId: parseInt(shipmentId ?? '0', 10),
  });
  const {
    data: paymentSuccess,
    isFetching: isPollingPayment,
    refetch: pollPayment,
  } = usePollPaymentQuery({
    ElectronicPaymentType: electronicPaymentType ?? integratedDebitProvider ?? '',
    ShipmentId: parseInt(shipmentId ?? '0', 10),
  });
  const { isPending: isPrintingReceipt, mutate: printReceipt } = usePrintReceipt({
    Subtotal: subtotal,
    Total: total,
  });

  const handlePollPayment = useCallback(async () => {
    try {
      const result = await pollPayment();

      if (result.error) {
        if (result.error instanceof PollNotCompleteError) {
          const MAX_POLL_RECOVERY_ATTEMPTS = 1;
          if (pollRecoveryAttemptsRef.current < MAX_POLL_RECOVERY_ATTEMPTS) {
            // Retries exhausted with no final status yet - the payment may still complete once the
            // Hub's result lands server-side. Invalidate the cart query (GetCart) to force a refetch;
            // if that resolves the pending entry in activeCart.ExistingPayments, the page-refresh-
            // recovery effect below will pick it up on its own. Poll once more either way, bounded
            // to one extra attempt via pollRecoveryAttemptsRef.
            pollRecoveryAttemptsRef.current += 1;
            void queryClient.invalidateQueries({ queryKey: [GetCart, shipmentId] });
            void handlePollPayment();

            return;
          }
          // Still unresolved after a recovery attempt. The approved copy below assumes "closing out"
          // happens only after a refresh or support confirms the payment's actual status, not as an
          // unprompted manual tender: `ValidateTransaction` only blocks a second *electronic* Hub
          // attempt on this shipment, so adding a manual tender without that confirmation could
          // still double-pay. This should never happen outside a genuine Hub/backend outage, so log
          // it to Datadog for visibility into how often it actually occurs.
          logger.error(result.error, {
            message: `payment poll exhausted recovery attempts with no final status for shipment ${String(shipmentId)}`,
          });
          dispatch(
            errorNotification(
              'Payment failed. The customer may have been charged. Do not run the card again. Refresh the register and try closing out the order, or contact LeisurePay support to confirm payment status.',
            ),
          );
        } else {
          // result.error can be a plain string (http.ts's `post()` rejects with strings) or an
          // Error, so `.message` alone isn't enough to always get the real message.
          let errorMessage: string | undefined;
          if (typeof result.error === 'string') {
            errorMessage = result.error;
          } else if (result.error instanceof Error) {
            errorMessage = result.error.message;
          }
          dispatch(
            errorNotification(`Payment failed - Unable to retrieve payment status: ${errorMessage ?? 'Unknown error'}`),
          );
        }
        setIsPollingComplete(true); // Mark as complete to prevent infinite polling
      } else if (result.data) {
        // If we got data back, polling is complete
        setIsPollingComplete(true);
        if (!result.data.PaymentSuccess) {
          // When the error messaging updates LD flag is enabled, a Hub-initiated Pay By Bank
          // decline with a structured decline message set (BudtenderMessage/ErrorCode) is surfaced
          // via the driver-facing DutchiePayErrorMessage banner instead of the generic toast.
          if (isDutchiePayDeclineMessagingEnabled && result.data.BudtenderMessage) {
            dispatch(setIsDutchiePayError(true));
            dispatch(
              setDutchiePayErrorMessage(
                result.data.ErrorCode
                  ? `${result.data.BudtenderMessage} (Code: ${result.data.ErrorCode})`
                  : result.data.BudtenderMessage,
              ),
            );
          } else {
            dispatch(errorNotification(result.data.PollingError || 'Payment was not successful'));
          }
        }
      } else {
        // Neither error nor data - this shouldn't happen but handle gracefully
        dispatch(errorNotification('Payment status unknown - please try again'));
        setIsPollingComplete(true); // Mark as complete to prevent infinite polling
      }
    } catch (error) {
      // Handle the case where the query rejects (line 50 in poll-payment.ts)
      // This happens when polling isn't complete yet, which is normal behavior
      // We don't want to show an error message for this case
      console.log('Polling not complete yet, will retry...');
    }
    // Memoized so the polling effect below (which lists this in its deps) doesn't restart its
    // poll on every unrelated re-render.
  }, [dispatch, isDutchiePayDeclineMessagingEnabled, pollPayment, queryClient, setIsPollingComplete, shipmentId]);

  const { isPending: isPaying, mutate: pay } = usePay({
    ElectronicPaid: electronicPaid,
    ElectronicPaymentType: electronicPaymentType ?? integratedDebitProvider ?? '',
    ...(electronicPaymentType?.startsWith('generipay-') && { ElectronicPaymentData: electronicPaymentData }),
    onSuccess: (resp) => {
      if (!resp) {
        dispatch(errorNotification('Error processing payment'));
        return;
      }

      if (resp.IsPolling) {
        void handlePollPayment();
      } else {
        dispatch(setActiveCart({ ...activeCart, TipAmount: resp.TipAmount }));

        // Handle Hub payments like DutchiePay payments, not like debit payments
        if (electronicPaymentType === 'paymentshub') {
          // For Hub payments, set isPaid to true and handle like a preauth payment
          setIsPaid(true);
        } else {
          // For regular debit payments, use the existing flow
          onPayDebitSuccess();
        }
      }
    },
  });

  const {
    data: cartDetail,
    isFetching: isLoadingCartDetail,
    refetch: getCartDetailsAgain,
  } = useCart({
    FeePaymentMethodIds: debit > 0 ? [FeePaymentMethod.PinDebit] : [],
    registerId: selectedRegister?.id,
  });

  useEffect(() => {
    if (cartDetail) {
      // TODO: this shouldn't be necessary.. just use the hook. mind the tipAmount being set above though...
      // finish removing activeCustomer from the store first?
      dispatch(setActiveCart(cartDetail));

      setIsPaid(!!cartDetail?.Payment?.ElectronicAmount || !!debit);
      for (const payment of cartDetail.ExistingPayments) {
        if (payment.type === 2) {
          setDebit(payment.amount);
          setIsPaid(true);
        }
        // TODO: add credit here for rethink
        // else if(payment.type === 1) {
        //   setCredit(payment.amount);
        // }
      }
      if (!isDutchiePayError) {
        dispatch(
          setIsDutchiePayError((cartDetail?.ExistingPayments?.length ?? 0) > 0 || !isAllowDutchiePayDeliveryFFEnabled),
        );
      }

      if (!isRethinkError) {
        dispatch(setIsRethinkError((cartDetail?.ExistingPayments?.length ?? 0) > 0 || !isUseRethinkIntegrationEnabled));
      }
    }
  }, [cartDetail]);

  const { isLoading: isLoadingDeliveryInfo, refetch: getDeliveryInfoAgain } = useDeliveryInfo();

  const prepaymentType =
    preauthAmt > 0 ? (isDutchiePay ? PaymentType.Dutchiepay : isRethink ? PaymentType.Rethink : '') : '';

  const { isPending: isCheckingOut, mutateAsync: checkout } = useCheckout({
    onError: (ex: Error) => {
      dispatch(setIsCheckoutComplete(false));
      setShowPbbInstoreSuccess(false);
      const isPbbError = isDutchiePayDeclineMessagingEnabled
        ? isDutchiePayCheckoutError(ex, prepaymentType)
        : ex.message.includes('Pay By Bank') || prepaymentType === PaymentType.Dutchiepay;
      if (isPbbError) {
        dispatch(setIsDutchiePayError(true));
        dispatch(setDutchiePayErrorMessage(ex.message));
        void openCart();
      } else if (/Re[Tt]hink/g.test(ex.message)) {
        dispatch(setIsRethinkError(true));
        dispatch(setRethinkErrorMessage(ex.message));
        void openCart();
      } else {
        dispatch(errorNotification(ex.message || 'Checkout failed'));
      }
      // Finally, throw the error for caller to handle failed checkout
      onFailure();
    },
    onSuccess: () => {
      dispatch(setIsCheckoutComplete(true));

      void queryClient.invalidateQueries({ queryKey: [GetOrdersForRouteKey, selectedRouteId] });
      void queryClient.invalidateQueries({ queryKey: [GuestListKey, selectedRegister?.id] });

      if ((isPrintWorkerEnabled && selectedReceiptPrinterGuid) || (!isPrintWorkerEnabled && selectedReceiptPrinterId)) {
        printReceipt();
      }

      onPaymentSuccess();
    },
  });

  async function openCart() {
    dispatch(setIsCheckoutComplete(false));
    await getCartDetailsAgain();
    await getDeliveryInfoAgain();
  }

  const onPayDebitSuccess = useCallback(() => {
    void dispatch(successNotification('Electronic payment success'));

    const pinDebitFeeIds = (activeCart?.FeesByPaymentType ?? [])
      .filter((x) => x.FeePaymentMethod === FeePaymentMethod.PinDebit)
      .map((x) => x.FeeDonationId);

    // Always refresh the cart for PBB payments to get updated fee information
    // For other payment types, only refresh if there are pin debit fees
    // TODO: Refresh the cart for ALL payment types once we start adding new ones and confirm it is safe
    if (isDutchiePay || Number(pinDebitFeeIds) > 0) {
      void openCart();
    }
  }, [dispatch, activeCart, openCart, isDutchiePay]);

  const hasDebitPaymentFeeApplied = useMemo(() => {
    const hasFee = activeCart?.FeesDonations?.some((x) => x.FeePaymentMethod === FeePaymentMethod.PinDebit);

    return hasFee;
  }, [activeCart?.FeesDonations]);

  const potentialNonCashAdjustments = useMemo(() => {
    const adjustments =
      (activeCart?.FeesByPaymentType ?? [])
        .filter((fd) => fd.FeePaymentMethod === FeePaymentMethod.PinDebit && !hasDebitPaymentFeeApplied)
        .map((fd) => fd.CashValue)
        .reduce((sum, cv) => sum + cv, 0) ?? 0;

    return adjustments;
  }, [activeCart?.FeesByPaymentType, hasDebitPaymentFeeApplied]);
  const paymentMethodFallback = !hasPreauth && !validationNeeded;

  const displayTotal = useMemo(() => {
    const calculatedTotal = total + (tip || 0) + (paymentMethodFallback && debit > 0 ? potentialNonCashAdjustments : 0);

    return calculatedTotal;
  }, [total, tip, paymentMethodFallback, debit, potentialNonCashAdjustments]);

  useEffect(() => {
    dispatch(setIsCheckoutComplete(false));
  }, []);

  useEffect(() => {
    if (hasPreauth && (activeCart?.ExistingPayments.length ?? 0) === 0) {
      // Don't overwrite preauthAmt if it's a Hub payment that we just set
      if (electronicPaymentType !== 'paymentshub') {
        setPreauthAmt(activeCart?.PreauthInfo.PreauthAmount ?? 0);
      }
    }
  }, [hasPreauth, activeCart, setPreauthAmt, electronicPaymentType]);

  useEffect(() => {
    // Page-refresh recovery: resume polling a pending payment from the cart. FINTECH-5030:
    // `PaymentProviderType` can be a Hub sub-type label ('paymentshub-debit'/'paymentshub-rethink')
    // that PaymentProcessorFactory doesn't understand (400s), so normalize it to 'paymentshub' -
    // without clobbering a selection already made this session.
    const existingPayment = activeCart?.ExistingPayments?.find((x) => !x.PollingComplete);
    if (!existingPayment?.PaymentProviderType) {
      return;
    }
    const providerType = existingPayment.PaymentProviderType.startsWith('paymentshub')
      ? 'paymentshub'
      : existingPayment.PaymentProviderType;
    setElectronicPaymentType((prev) => prev ?? providerType);
  }, [activeCart, setElectronicPaymentType]);

  useEffect(() => {
    // Reset polling complete state when payment type changes
    if (electronicPaymentType) {
      setIsPollingComplete(false);
      pollRecoveryAttemptsRef.current = 0;
    }
  }, [electronicPaymentType]);

  useEffect(() => {
    // This is really designed to handle cases where the page refreshes during payment processing
    // It ensures that we kick off polling so that we can complete checkout
    if (electronicPaymentType && !isPollingComplete) {
      void handlePollPayment();
    }
  }, [electronicPaymentType, handlePollPayment, isPollingComplete]);

  useEffect(() => {
    if (electronicPaid) {
      if (!selectedRegister?.id || selectedRegister.id === 0) {
        dispatch(errorNotification('No register selected, please select a register'));
        dispatch(setActiveCart(null));
        navigate(-1);
      } else {
        pay();
      }
    }
  }, [electronicPaid, navigate, pay, dispatch, selectedRegister]);

  useEffect(() => {
    // if we ever implement credit (planet payments?) in curbside this will need to account for credit
    if (paymentSuccess?.PaymentSuccess && !isPaid) {
      setIsPaid(true);
      setDebit(paymentSuccess.DebitPaid);

      const updatedCart = { ...activeCart, TipAmount: paymentSuccess.TipAmount ?? 0 };

      dispatch(setActiveCart(updatedCart));
      onPayDebitSuccess();
    }
    // We removed onPayDebitSuccess from the deps intentionally becuase adding it causes an infinite render loop
  }, [paymentSuccess]);

  if (showCanPayCheckout) {
    return (
      <CanPayCheckout
        amount={canpayPaid}
        onPay={() => {
          setShowCanPayCheckout(false);
          setIsPaid(true);
        }}
      />
    );
  }

  if (showPbbInstoreQr) {
    return (
      <PayByBankInstoreQrScreen
        instoreCartUrl={instoreCartUrl}
        isLoading={isPbbInstoreLoading}
        isConfirming={isPbbInstoreConfirming}
        error={pbbInstoreError}
        onBack={() => setShowPbbInstoreQr(false)}
        onRetry={() => setShowPbbInstoreQr(false)}
      />
    );
  }

  const initiateCheckout = async (
    cashPaid: number,
    debitPaid: number,
    preauthPaid: number,
    total: number,
    giftPaid: number,
    generiPayPaid: Record<string, string>,
    checkPaidAmount: number,
    manualPaymentValue: ManualPaymentSubmission | undefined,
  ) => {
    logger.info(`initiate checkout for shipment ${String(shipmentId)}`, {
      cart: activeCart,
      key: customEventKeys.cart.checkout,
      shipmentId,
    });

    try {
      const tip = activeCart?.TipAmount ?? 0;

      preauthPaid = preauthPaid > 0 && hasPreauth ? total + tip : 0;
      dispatch(setIsCheckoutComplete(false)); // assume failure

      // At most one PaymentMethods entry, from the single manual payment set via the modal -
      // webapi's CartCheckoutService derives req.ManualPayments from this array server-side
      // (PaymentMethods.GetTvp), matching processors by `Name === 'manual'` and reading
      // `Data.ManualPaymentProcessorId`/Amount off each entry - nothing else needs to be sent.
      const manualPaymentMethods =
        manualPaymentValue && manualPaymentValue.amount > 0
          ? [
              {
                amount: manualPaymentValue.amount,
                data: {
                  ManualPaymentProcessorId: manualPaymentValue.processorId,
                  ManualPaymentProcessorName: manualPaymentValue.processorName,
                },
                id: `manual-${String(manualPaymentValue.processorId)}`,
                name: 'manual',
                type: PaymentTypeModel.Manual,
              },
            ]
          : [];
      const manualPaidTotalAmount = manualPaymentValue?.amount ?? 0;

      const paid = cashPaid + debitPaid + giftPaid + checkPaidAmount + manualPaidTotalAmount + preauthPaid;
      let change = paid - tip - total;
      if (change < 0) {
        change = 0;
      }
      dispatch(setChangeAmountDue(roundChangeDue(location?.Features ?? {}, change)));
      dispatch(setChangeAmountRounded(changeRounded));
      setPreauthAmt(preauthPaid);
      dispatch(setTotalAmountPaid(paid));

      await openCart();

      await checkout({
        CCPaid: prepaymentType === PaymentType.Rethink ? preauthPaid : null,
        CheckPaid: checkPaidAmount,
        DebitPaid: debitPaid,
        DueCustomer: roundChangeDue(location?.Features ?? {}, change),
        DueCustomerRounded: changeRounded,
        GiftPaid: giftPaid,
        OrderId: activeCart?.PreauthInfo?.OrderId ?? 0,
        Paidamt: cashPaid,
        PaymentMethods: manualPaymentMethods,
        // The below properties added to implement Dutchie Pay
        // The order Id is required for finalization
        PreAuth: preauthPaid ?? 0,
        PrepaymentType: prepaymentType,
        SubTotal: subtotal,
        TaxAmt: tax,
        TotalCharges: total,
      } as CheckoutRequest);
    } catch (error) {
      logger.error(error, { message: `checkout error for shipment ${String(shipmentId)}` });
      onFailure();
    }
  };

  // Helper method to dynamically create key names based on GeneriPay integration type
  // e.g., 'spin' -> 'generipay-spin' or 'wedge' -> 'generipay-wedge'
  const createGeneriPayKey = (integrationType: string): string => {
    return `generipay-${integrationType}`;
  };

  const payExact = (type: string) => {
    setCash(type === 'cash' ? total : 0);
    setGiftcardPaid(type === 'giftcard' ? total : 0);
    setDebit(type === 'debit' ? total + potentialNonCashAdjustments : 0);
    setCanpayPaid(type === 'canpay' ? total : 0);
    setCheckPaid(type === 'check' ? total : 0);

    if (type.startsWith('generipay-')) {
      setGeneriPayPaid((prev) => ({ ...prev, [type]: total.toString() }));
    }
    setPreauthAmt(0);
  };

  const handleGeneriPayAdd = (integrationType: string) => {
    if (!selectedRegister?.id || selectedRegister.id === 0) {
      dispatch(errorNotification('No register selected, please select a register'));
      dispatch(setActiveCart(null));
      navigate(-1);
    } else {
      setElectronicPaymentType(createGeneriPayKey(integrationType));
      setElectronicPaid(generiPayPaid[createGeneriPayKey(integrationType)] ?? '0');
      setElectronicPaymentData({
        Token: '',
        PaymentIntegrationDefinitionId:
          generiPayButtons?.find((g) => g.IntegrationType === integrationType)?.PaymentIntegrationDefinitionId ?? '',
        ShipmentPaymentType:
          generiPayButtons?.find((g) => g.IntegrationType === integrationType)?.PosReportingFieldId ?? 0,
        UseCartSync: generiPayButtons?.find((g) => g.IntegrationType === integrationType)?.UseCartSync ?? false,
      });
    }
  };

  const handlePaymentsHub = async (total) => {
    if (!selectedRegister?.id || selectedRegister.id === 0) {
      dispatch(errorNotification('No register selected, please select a register'));
      dispatch(setActiveCart(null));
      navigate(-1);
      return false;
    }

    try {
      setElectronicPaymentType('paymentshub');
      setPreauthAmt(total);
      setElectronicPaid(total.toString()); // Set electronicPaid for Hub payments

      // Update the activeCart with preauth info so hooks can detect it properly
      if (activeCart) {
        const updatedCart = {
          ...activeCart,
          PreauthInfo: {
            ...activeCart.PreauthInfo,
            PreauthAmount: total,
            PaymentType: PaymentType.Dutchiepay, // Treat Hub payments like DutchiePay
            OrderId: activeCart.PreauthInfo?.OrderId ?? 0,
          },
          Payment: {
            ...activeCart.Payment,
            ElectronicType: 'paymentshub',
            ElectronicAmount: total,
          },
        };
        dispatch(setActiveCart(updatedCart));
      }

      return true;
    } catch (error) {
      dispatch(errorNotification('Hub payment failed. Please try again.'));
      return false;
    }
  };

  const showDebitPayButton = !!integratedDebitProvider && !isPaid && debit > 0;
  const showCanPayPayButton = isAcceptCanPayFFEnabled && !isPaid && canpayPaid > 0 && !showDebitPayButton;
  const showCheckoutButton = !showDebitPayButton && !showCanPayPayButton && !isRethink;
  const showGeneriPayButtons = (generiPayButtons?.length ?? 0) > 0;

  const manualPaidTotal = manualPayment?.amount ?? 0;

  const canCheckOut =
    hasPreauth || debit + cash + canpayPaid + giftcardPaid + checkPaid + manualPaidTotal >= displayTotal;

  const dutchiePrepaidWithError = paymentMethodFallback ? 0 : preauthAmt;

  let change =
    debit +
    cash +
    canpayPaid +
    giftcardPaid +
    checkPaid +
    manualPaidTotal +
    dutchiePrepaidWithError -
    total -
    (debit > 0 ? potentialNonCashAdjustments : 0) -
    tip;

  let remaining =
    displayTotal - debit - cash - canpayPaid - giftcardPaid - checkPaid - manualPaidTotal - dutchiePrepaidWithError;

  remaining = Math.max(0, remaining);
  change = Math.max(0, change);

  const changeRounded = getChangeRounded(location?.Features ?? {}, change);

  const isLoading =
    isPollingPayment || isPrintingReceipt || isPaying || isLoadingCartDetail || isLoadingDeliveryInfo || isCheckingOut;

  return (
    <>
      <>
        {showPbbInstoreSuccess && (
          <PbbInstoreSuccessCard data-testid='pbb-instore-success-overlay'>
            <PayByBankLogo />
            <PbbInstoreSuccessHeading>Success, order confirmed!</PbbInstoreSuccessHeading>
            <PbbInstoreSuccessBody>Click &ldquo;Complete&rdquo; below to finalize this payment.</PbbInstoreSuccessBody>
          </PbbInstoreSuccessCard>
        )}
        {isDutchiePay && !showPbbInstoreSuccess && (
          <DutchiePayMessage data-testid='dutchie-pay-overlay'>
            <PayByBankLogo />
            <p>
              This order was paid for using Pay By Bank. No payment selection is needed. To complete this transaction,
              simply tap the <b>"Pay"</b> button below.
            </p>
          </DutchiePayMessage>
        )}
        {!isDutchiePay && isDutchiePayError && dutchiePayErrorMessage !== '' && (
          <DutchiePayErrorMessageContainer data-testid='dutchie-pay-error-overlay'>
            <div>
              <ErrorIcon />
            </div>
            <DutchiePayErrorMessage>
              <p>{dutchiePayErrorMessage}</p>
            </DutchiePayErrorMessage>
          </DutchiePayErrorMessageContainer>
        )}
        {isRethink && (
          <RethinkBanner data-testid='rethink-overlay'>
            <div>
              <InfoIcon />
            </div>
            <p>This order has been paid using a card. To complete this order click “Complete Order” below.</p>
          </RethinkBanner>
        )}
        {!isRethink && isRethinkError && rethinkErrorMessage !== '' && (
          <DutchiePayErrorMessageContainer data-testid='rethink-error-overlay'>
            <div>
              <ErrorIcon />
            </div>
            <DutchiePayErrorMessage>
              <p>{rethinkErrorMessage}</p>
            </DutchiePayErrorMessage>
          </DutchiePayErrorMessageContainer>
        )}
        {!validationNeeded && (
          <PaymentMethodContainer>
            {paymentMethodFallback && !showPbbInstoreSuccess && (
              <>
                {!isCurbsideCashlessFFEnabled && (
                  <InputContainer>
                    <InputLabel>Cash:</InputLabel>
                    <InputRowContainer>
                      <DutchieInput
                        allowNegative={false}
                        automationId='custom-cash-input'
                        decimalScale={2}
                        fixedDecimalScale
                        getInputRef={cashInputRef}
                        inputMode='decimal'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCash(Number(e.target.value))}
                        onFocus={() => {
                          cashInputRef.current?.setSelectionRange(0, 0);
                        }}
                        placeholder='...'
                        startAdornment={<DollarInput>$</DollarInput>}
                        type='number'
                        value={cash}
                      />
                      <ExactButton
                        data-dd-action-name='Exact (cash)'
                        data-testid='exact-cash-button'
                        disabled={isPaid}
                        onClick={() => payExact('cash')}
                      >
                        Exact
                      </ExactButton>
                    </InputRowContainer>
                  </InputContainer>
                )}
                {isDebitEnabled && (
                  <>
                    {debitDisabled && <DebitWarning />}
                    <InputContainer>
                      <InputLabel>Debit:</InputLabel>
                      <InputRowContainer>
                        <DutchieInput
                          allowNegative={false}
                          automationId='custom-debit-input'
                          decimalScale={2}
                          disabled={isPaid || debitDisabled}
                          fixedDecimalScale
                          getInputRef={debitInputRef}
                          inputMode='decimal'
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const debitValue = Number(e.target.value);
                            const shouldApplySurcharge = !!debitValue;

                            setAddedSurcharge(shouldApplySurcharge);
                            setDebit(debitValue);
                          }}
                          onFocus={() => {
                            debitInputRef.current?.setSelectionRange(0, 0);
                          }}
                          placeholder='...'
                          startAdornment={<DollarInput>$</DollarInput>}
                          type='number'
                          value={debit}
                        />
                        <ExactButton
                          data-dd-action-name='Exact (debit)'
                          data-testid='exact-debit-button'
                          disabled={isPaid || debitDisabled}
                          onClick={() => payExact('debit')}
                        >
                          Exact
                        </ExactButton>
                      </InputRowContainer>
                      {potentialNonCashAdjustments > 0 && (
                        <NonCashFeeContainer>
                          {!addedSurcharge
                            ? `$${potentialNonCashAdjustments.toFixed(2)} ${String(paymentFeeName)} fee will be added to
                      order total.`
                            : `$${potentialNonCashAdjustments.toFixed(
                                2,
                              )} ${String(paymentFeeName)} fee has been added to order total.`}
                        </NonCashFeeContainer>
                      )}
                    </InputContainer>
                  </>
                )}

                {isAcceptCanPayFFEnabled && (
                  <InputContainer>
                    <InputLabel>CanPay:</InputLabel>
                    <InputRowContainer>
                      <DutchieInput
                        allowNegative={false}
                        automationId='custom-canpay-input'
                        decimalScale={2}
                        disabled={isPaid}
                        fixedDecimalScale
                        getInputRef={canPayInputRef}
                        inputMode='decimal'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCanpayPaid(Number(e.target.value))}
                        onFocus={() => {
                          canPayInputRef.current?.setSelectionRange(0, 0);
                        }}
                        placeholder='...'
                        startAdornment={<DollarInput>$</DollarInput>}
                        type='number'
                        value={canpayPaid}
                      />
                      <ExactButton
                        data-dd-action-name='Exact (canpay)'
                        data-testid='exact-canpay-button'
                        disabled={isPaid}
                        onClick={() => payExact('canpay')}
                      >
                        Exact
                      </ExactButton>
                    </InputRowContainer>
                  </InputContainer>
                )}

                {isCheckOutGiftCardFFEnabled && (
                  <InputContainer>
                    <InputLabel>Gift Card:</InputLabel>
                    <InputRowContainer>
                      <DutchieInput
                        allowNegative={false}
                        automationId='custom-giftcard-input'
                        decimalScale={2}
                        disabled={isPaid}
                        fixedDecimalScale
                        getInputRef={giftCardInputRef}
                        inputMode='decimal'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGiftcardPaid(Number(e.target.value))}
                        onFocus={() => {
                          giftCardInputRef.current?.setSelectionRange(0, 0);
                        }}
                        placeholder='...'
                        startAdornment={<DollarInput>$</DollarInput>}
                        type='number'
                        value={giftcardPaid}
                      />
                      <ExactButton
                        data-dd-action-name='Exact (gift card)'
                        data-testid='exact-giftcard-button'
                        disabled={isPaid}
                        onClick={() => payExact('giftcard')}
                      >
                        Exact
                      </ExactButton>
                    </InputRowContainer>
                  </InputContainer>
                )}
                {isCheckEnabled && (
                  <InputContainer>
                    <InputLabel>Check:</InputLabel>
                    <InputRowContainer>
                      <DutchieInput
                        allowNegative={false}
                        automationId='custom-check-input'
                        decimalScale={2}
                        disabled={isPaid}
                        fixedDecimalScale
                        getInputRef={checkInputRef}
                        inputMode='decimal'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCheckPaid(Number(e.target.value))}
                        onFocus={() => {
                          checkInputRef.current?.setSelectionRange(0, 0);
                        }}
                        placeholder='...'
                        startAdornment={<DollarInput>$</DollarInput>}
                        type='number'
                        value={checkPaid}
                      />
                      <ExactButton
                        data-dd-action-name='Exact (check)'
                        data-testid='exact-check-button'
                        disabled={isPaid}
                        onClick={() => payExact('check')}
                      >
                        Exact
                      </ExactButton>
                    </InputRowContainer>
                  </InputContainer>
                )}
                {isManualPaymentsFeatureEnabled && enabledManualProcessors.length > 0 && (
                  <InputContainer>
                    {manualPayment ? (
                      <>
                        <InputLabel>Manual ({manualPayment.processorName}):</InputLabel>
                        <InputRowContainer>
                          <ManualPaymentSummary data-testid='manual-payment-summary'>
                            ${manualPayment.amount.toFixed(2)}
                          </ManualPaymentSummary>
                          <ExactButton
                            data-dd-action-name='Edit (manual)'
                            data-testid='edit-manual-button'
                            disabled={isPaid}
                            onClick={() => setShowManualPaymentModal(true)}
                          >
                            Edit
                          </ExactButton>
                          <ExactButton
                            data-dd-action-name='Remove (manual)'
                            data-testid='remove-manual-button'
                            disabled={isPaid}
                            onClick={() => setManualPayment(undefined)}
                          >
                            Remove
                          </ExactButton>
                        </InputRowContainer>
                      </>
                    ) : (
                      <InputRowContainer>
                        <Button
                          automationId='manual-payment-button'
                          disabled={isPaid}
                          fullWidth
                          label='Manual'
                          onClick={() => setShowManualPaymentModal(true)}
                        />
                      </InputRowContainer>
                    )}
                  </InputContainer>
                )}
                {showGeneriPayButtons &&
                  isGeneriPaySpinEnabled &&
                  generiPayButtons
                    ?.filter((g) => g.IntegrationType === 'spin')
                    .map((generiPayButton) => (
                      <InputContainer key={generiPayButton.IntegrationType}>
                        <InputLabel>{generiPayButton.PosButtonText}:</InputLabel>
                        <InputRowContainer>
                          <DutchieInput
                            allowNegative={false}
                            data-testid={`custom-generipay-${generiPayButton.IntegrationType}-input`}
                            decimalScale={2}
                            disabled={isPaid}
                            fixedDecimalScale
                            getInputRef={(el) => (generiPayInputRefs.current[generiPayButton.IntegrationType] = el)}
                            inputMode='decimal'
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setGeneriPayPaid((prev) => ({
                                ...prev,
                                [createGeneriPayKey(generiPayButton.IntegrationType)]: e.target.value,
                              }))
                            }
                            onFocus={() => {
                              generiPayInputRefs.current[generiPayButton.IntegrationType]?.setSelectionRange(0, 0);
                            }}
                            placeholder='...'
                            startAdornment={<DollarInput>$</DollarInput>}
                            type='number'
                            value={generiPayPaid[createGeneriPayKey(generiPayButton.IntegrationType)] ?? ''}
                          />
                          {Number(generiPayPaid[createGeneriPayKey(generiPayButton.IntegrationType)] ?? '0') === 0 && (
                            <ExactButton
                              data-dd-action-name={`Exact (${generiPayButton.PosButtonText})`}
                              data-testid={`exact-${generiPayButton.IntegrationType}-button`}
                              disabled={isPaid}
                              onClick={() => payExact(createGeneriPayKey(generiPayButton.IntegrationType))}
                            >
                              Exact
                            </ExactButton>
                          )}
                          {Number(generiPayPaid[createGeneriPayKey(generiPayButton.IntegrationType)] ?? '0') > 0 && (
                            <ExactButton
                              data-dd-action-name={`Add (${generiPayButton.PosButtonText})`}
                              data-testid={`add-${generiPayButton.IntegrationType}-button`}
                              disabled={isPaid}
                              onClick={() => handleGeneriPayAdd(generiPayButton.IntegrationType)}
                            >
                              Add
                            </ExactButton>
                          )}
                        </InputRowContainer>
                      </InputContainer>
                    ))}
                {isPaymentsHubEnabled && (
                  <InputContainer>
                    <InputRowContainer>
                      <Button
                        fullWidth
                        label='Pay with DutchieHub'
                        onClick={() => handlePaymentsHub(total)}
                        // Explicitly not supporting a split between a Manual/Check tender and a
                        // Hub-based payment for now - untested combination, and matches register's
                        // own behavior of hiding every other tender button once a preauth exists
                        // (see PaymentButtons.tsx's `{!isPreAuthOrder && (...)}` wrapping the whole
                        // button row). This only covers the reverse order (Manual/Check first, then
                        // Hub) - the other direction is already covered for free, since the whole
                        // Cash/Debit/Check/Manual block already disappears once hasPreauth is true.
                        disabled={isPaid || checkPaid > 0 || !!manualPayment}
                      />
                    </InputRowContainer>
                  </InputContainer>
                )}
                {isPbbInstoreEnabled && isLocationPbbEnabled && !showPbbInstoreSuccess && (
                  <InputContainer>
                    <InputRowContainer>
                      <Button
                        data-testid='pbb-instore-button'
                        fullWidth
                        // See the DutchieHub button above - same reasoning, extending the existing
                        // debit/cash guard to also cover the new Check/Manual tenders.
                        disabled={isPaid || debit > 0 || cash > 0 || checkPaid > 0 || !!manualPayment}
                        onClick={() => setShowPbbInstoreQr(true)}
                        label='Pay By Bank'
                      />
                    </InputRowContainer>
                  </InputContainer>
                )}
              </>
            )}
          </PaymentMethodContainer>
        )}
      </>
      {totals && (
        <TotalSummaryContainer>
          <OrderDetailsWrapper>
            <StyledLabel>Order details</StyledLabel>
          </OrderDetailsWrapper>
          <CartSummary displayNonCashFee={(paymentMethodFallback && !!debit) || isRethink} totals={totals} />
        </TotalSummaryContainer>
      )}
      <OrderDetailsContainer>
        <CartOrderTotal
          cash={cash}
          change={roundChangeDue(location?.Features ?? {}, change)}
          changeRounded={changeRounded}
          debit={debit}
          displayTotal={displayTotal}
          paymentMethodFallback={paymentMethodFallback}
          remaining={remaining}
        />
        {!validationNeeded && (
          <>
            {showPbbInstoreSuccess && (
              <ActionButton
                buttonSize='extra-large'
                ddActionName='Complete (PBB instore)'
                fullWidth
                label='Complete'
                loading={isLoading}
                onClick={() =>
                  initiateCheckout(
                    cash,
                    debit,
                    preauthAmt,
                    total,
                    giftcardPaid,
                    generiPayPaid,
                    checkPaid,
                    manualPayment,
                  )
                }
              />
            )}
            {showCheckoutButton && !showPbbInstoreSuccess && (
              <ActionButton
                buttonSize='extra-large'
                ddActionName='Pay (standard)'
                disabled={!canCheckOut}
                fullWidth
                label='Pay'
                loading={isLoading}
                onClick={() =>
                  initiateCheckout(
                    cash,
                    debit,
                    preauthAmt,
                    total,
                    giftcardPaid,
                    generiPayPaid,
                    checkPaid,
                    manualPayment,
                  )
                }
              />
            )}
            {isRethink && (
              <ActionButton
                buttonSize='extra-large'
                ddActionName='Pay (Rethink)'
                disabled={!canCheckOut}
                fullWidth
                label='Complete Order'
                loading={isLoading}
                onClick={() =>
                  initiateCheckout(
                    cash,
                    debit,
                    preauthAmt,
                    total,
                    giftcardPaid,
                    generiPayPaid,
                    checkPaid,
                    manualPayment,
                  )
                }
              />
            )}
            {showDebitPayButton && !showPbbInstoreSuccess && (
              <ActionButton
                buttonSize='extra-large'
                ddActionName='Pay (debit)'
                fullWidth
                label='Pay'
                loading={isLoading}
                onClick={() => setElectronicPaid(debit.toFixed(2))}
              />
            )}
            {showCanPayPayButton && !showPbbInstoreSuccess && (
              <ActionButton
                buttonSize='extra-large'
                ddActionName='Pay (CanPay)'
                fullWidth
                label='Pay'
                loading={isLoading}
                onClick={() => setShowCanPayCheckout(true)}
              />
            )}
          </>
        )}
      </OrderDetailsContainer>
      <ManualPaymentModal
        onClose={() => setShowManualPaymentModal(false)}
        onSubmit={(submission) => {
          setManualPayment(submission);
          setShowManualPaymentModal(false);
        }}
        open={showManualPaymentModal}
        processors={enabledManualProcessors}
        remaining={remaining}
      />
    </>
  );
};

const DutchiePayMessage = styled.div`
  border: 1px solid #d8dee3;
  font-family: Lato, Matter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  padding: 1.875rem 1.25rem;
  border-radius: 0.375rem;
  display: flex;
  flex-direction: column;
  align-items: center;

  & > svg {
    width: 200px;
    margin-bottom: 1.25rem;
  }

  & > p {
    text-align: center;
    margin: 0;
  }
`;

const RethinkBanner = styled.div`
  font-family: Lato, Matter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  padding: 1rem;
  background-color: #edf1fa;
  border: none;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;

  & > svg {
    width: 200px;
    margin-bottom: 1.25rem;
  }

  & > p {
    text-align: left;
    margin: 0;
  }
`;

const ActionButton = styled(Button)`
  margin: 2rem 0;
`;

export const NonCashAdjustmentFeeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 16px;
  background: var(--color-blue-10);
  border-radius: 8px;
  flex: none;
  order: 0;
  flex-grow: 1;
  margin-bottom: 1.5rem;
  margin-top: -1rem;
`;

export const NonCashAdjustmentFeeText = styled.span`
  font-weight: 400;
  font-family: 'Matter', sans-serif;
  font-style: normal;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.005em;
  color: var(--color-blue-80);
  padding-left: 8px;
`;

export const NonCashAdjustmentFeeWeightedText = styled.span`
  font-family: 'Matter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.005em;
  color: var(--color-blue-80);
`;

export const DutchiePayErrorMessageContainer = styled.div`
  background-color: #fae8ea;
  border-radius: 0.5rem;
  width: 100%;
  padding: 0.5rem 1rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1rem;
`;

export const DutchiePayErrorMessage = styled.div`
  line-height: 1.125rem;
  font-size: 0.8125rem;
  font-weight: 400;
  color: #be0517;
  p {
    margin-bottom: 0;
  }
`;

const PaymentMethodContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputLabel = styled.div`
  color: #2c3236;
  font-size: 0.8125rem;
  line-height: 1.125rem;
  font-weight: 600;
`;

const InputRowContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ManualPaymentSummary = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex: 1;
`;

const ExactButton = styled.button<{ disabled?: boolean }>`
  background-color: var(--color-gray-20);
  color: #2e3538;
  border: none;
  padding: 0.75rem 1.25rem;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.25rem;
  border-radius: 0.375rem;

  ${({ disabled }) =>
    disabled &&
    `
    background-color: var(--color-gray-70);
    color: var(--color-gray-50);
    `}
`;

const NonCashFeeContainer = styled.div`
  font-size: 0.8125rem;
  line-height: 0.875rem;
  font-weight: 600;
  color: #646d72;
`;

const DollarInput = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #a0a8ab;
`;

const LabelCss = css`
  color: black;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 2rem;
`;

const StyledLabel = styled.div`
  ${LabelCss}
`;

const TotalSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const OrderDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  background-color: white;
  border-top: 1px solid #e3e6e7;
  bottom: 0;
`;

const OrderDetailsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
`;

const PbbInstoreSuccessCard = styled.div`
  border: 1px solid #d8dee3;
  border-radius: 0.375rem;
  padding: 1.875rem 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  margin-bottom: 1rem;

  & > svg {
    margin-bottom: 0.75rem;
  }
`;

const PbbInstoreSuccessHeading = styled.p`
  font-family: Lato, Matter, sans-serif;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--color-gray-90);
  margin: 0;
`;

const PbbInstoreSuccessBody = styled.p`
  font-family: Lato, Matter, sans-serif;
  font-size: 0.9375rem;
  font-weight: 400;
  color: var(--color-gray-90);
  max-width: 16rem;
  margin: 0;
`;
